import { useState } from 'react';
import { Sparkles, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { generateCalculatorReport } from '../lib/claude';
import SaveReportButton from './SaveReportButton';
import ReportMarkdown from './ReportMarkdown';
import type { CalculatorResult } from '../data/calculators';

interface AIReportProps {
  calculatorId: string;
  calculatorName: string;
  calculatorShortName: string;
  inputs: Record<string, unknown>;
  result: CalculatorResult;
}

export default function AIReport({ calculatorId, calculatorName, calculatorShortName, inputs, result }: AIReportProps) {
  const [report, setReport] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const aiReport = await generateCalculatorReport(calculatorName, inputs, result as unknown as Record<string, unknown>);
      setReport(aiReport);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  if (!report && !loading && !error) {
    return (
      <button onClick={generateReport} className="w-full mt-4 py-3 px-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center gap-2">
        <Sparkles className="w-5 h-5" />Generate AI Clinical Interpretation
      </button>
    );
  }

  if (loading) {
    return <div className="mt-4 p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200"><div className="flex items-center justify-center gap-3 text-purple-700"><Loader2 className="w-5 h-5 animate-spin" /><span>Generating AI interpretation...</span></div></div>;
  }

  if (error) {
    return (
      <div className="mt-4 p-4 bg-red-50 rounded-xl border border-red-200">
        <div className="flex items-center gap-2 text-red-700 mb-2"><AlertCircle className="w-5 h-5" /><span className="font-medium">Error</span></div>
        <p className="text-red-600 text-sm mb-3">{error}</p>
        <button onClick={generateReport} className="text-sm text-red-700 hover:text-red-800 flex items-center gap-1"><RefreshCw className="w-4 h-4" />Try again</button>
      </div>
    );
  }

  return (
    <div className="mt-4 p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200">
      <div className="flex items-center gap-2 text-purple-700 mb-4"><Sparkles className="w-5 h-5" /><span className="font-semibold">AI Clinical Interpretation</span></div>
      <div className="report-surface rounded-xl border border-purple-100 bg-white p-5 shadow-sm sm:p-6">
        <ReportMarkdown content={report ?? ''} />
      </div>
      <div className="mt-4 pt-4 border-t border-purple-200"><p className="text-xs text-gray-500 italic"><strong>Disclaimer:</strong> AI interpretation for decision support only. Always use clinical judgment.</p></div>
      <div className="mt-4 pt-4 border-t border-purple-200 flex flex-wrap items-center gap-3">
        <SaveReportButton reportData={{ calculator_id: calculatorId, calculator_name: calculatorName, calculator_short_name: calculatorShortName, score: result.score, risk_level: result.riskLevel, interpretation: result.interpretation, input_values: inputs, result_details: result.details, ai_interpretation: report || '' }} />
        <button onClick={generateReport} className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"><RefreshCw className="w-4 h-4" />Regenerate</button>
      </div>
    </div>
  );
}
